import { useState, useCallback, useMemo } from 'react';
import { Minus, Plus, PackageCheck, Warehouse, ExternalLink, Loader2 } from 'lucide-react';
import { Modal, Button } from '@/components/ui';
import { pickupApi } from '@/services/api/pickupApi';
import type { HandoffSource } from '@/services/api/pickupApi';
import type { OrderDetail, OrderItemDetail } from '@/types/models';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';

interface ReHandoffModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: OrderDetail;
  onSuccess: () => void;
}

const ReHandoffModal: React.FC<ReHandoffModalProps> = ({ isOpen, onClose, order, onSuccess }) => {
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [selectedSource, setSelectedSource] = useState<HandoffSource>('warehouse');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const remainingItems = useMemo(
    () => order.items.filter((item) => item.quantity_received - item.quantity_handed_off > 0),
    [order.items],
  );

  const remaining = useCallback(
    (item: OrderItemDetail) => item.quantity_received - item.quantity_handed_off,
    [],
  );

  const setItemQty = useCallback((id: number, qty: number) => {
    setQuantities((prev) => ({ ...prev, [id]: qty }));
  }, []);

  const hasAnyItems = remainingItems.some((i) => (quantities[i.id] ?? 0) > 0);
  const allFilled = remainingItems.length > 0 && remainingItems.every((i) => (quantities[i.id] ?? 0) === remaining(i));

  const fillAll = useCallback(() => {
    if (allFilled) {
      setQuantities({});
    } else {
      const filled: Record<number, number> = {};
      for (const item of remainingItems) {
        filled[item.id] = remaining(item);
      }
      setQuantities(filled);
    }
  }, [allFilled, remainingItems, remaining]);

  const canSubmit = hasAnyItems && notes.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      await pickupApi.confirmHandoff(order.id, {
        items: remainingItems.map((item) => ({
          order_item_id: item.id,
          quantity_handed_off: quantities[item.id] ?? 0,
        })),
        handoff_notes: notes,
        source: selectedSource,
      });
      toast.success('Товары довыданы!');
      setQuantities({});
      setNotes('');
      onSuccess();
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
            'Ошибка при довыдаче';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Довыдать заказ №${order.order_number}`}
      size="md"
    >
      <div className="space-y-5">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Товары к довыдаче
            </span>
            {remainingItems.length > 0 && (
              <button
                type="button"
                onClick={fillAll}
                className="text-xs font-medium text-amber-600 hover:text-amber-700 transition-colors"
              >
                {allFilled ? 'Сбросить' : 'Заполнить все'}
              </button>
            )}
          </div>

          {remainingItems.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-400">
              Все товары уже выданы
            </div>
          ) : (
            <div className="rounded-xl border border-gray-100 divide-y divide-gray-50 overflow-hidden">
              {remainingItems.map((item) => {
                const qty = quantities[item.id] ?? 0;
                const max = remaining(item);
                const isFull = qty === max;
                const isPartial = qty > 0 && qty < max;

                return (
                  <div
                    key={item.id}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2.5 transition-colors',
                      isFull ? 'bg-emerald-50/70' : isPartial ? 'bg-amber-50/50' : 'bg-white',
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <p className={cn('text-sm leading-tight truncate', isFull ? 'text-gray-500' : 'text-gray-800')}>
                        {item.name}
                      </p>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {item.sku}
                        <span className="mx-1">·</span>
                        выдано {item.quantity_handed_off} из {item.quantity_received}
                        <span className="mx-1">·</span>
                        <span className="text-amber-600">осталось {max}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setItemQty(item.id, Math.max(0, qty - 1))}
                        disabled={qty <= 0}
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className={cn(
                        'w-10 text-center text-sm font-mono tabular-nums font-medium',
                        isFull ? 'text-emerald-600' : isPartial ? 'text-amber-600' : 'text-gray-400',
                      )}>
                        {qty}
                      </span>
                      <button
                        type="button"
                        onClick={() => setItemQty(item.id, Math.min(max, qty + 1))}
                        disabled={qty >= max}
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-[11px] text-gray-400 ml-0.5 tabular-nums">
                        / {max}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Источник товара
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSelectedSource('warehouse')}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all',
                selectedSource === 'warehouse'
                  ? 'border-amber-300 bg-amber-50 text-amber-700 shadow-sm'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50',
              )}
            >
              <Warehouse className="w-4 h-4" />
              Со склада
            </button>
            <button
              type="button"
              onClick={() => setSelectedSource('external')}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all',
                selectedSource === 'external'
                  ? 'border-amber-300 bg-amber-50 text-amber-700 shadow-sm'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50',
              )}
            >
              <ExternalLink className="w-4 h-4" />
              Из другого источника
            </button>
          </div>
          {selectedSource === 'external' && (
            <p className="text-xs text-gray-400 mt-1.5 ml-1">
              Товар не будет списан со склада
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-amber-600 mb-1">
            Комментарий к довыдаче (обязательно)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Укажите причину довыдачи…"
            className="w-full px-3 py-2 text-sm border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
            rows={2}
          />
        </div>

        <div className="flex gap-3">
          <Button
            variant="ghost"
            className="flex-1"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Отмена
          </Button>
          <Button
            variant="primary"
            className="flex-1 !bg-amber-600 hover:!bg-amber-700"
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Довыдача…
              </>
            ) : (
              <>
                <PackageCheck className="w-4 h-4" />
                Довыдать
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReHandoffModal;
